/**
 * OEM Search — self-hosted lucide icon shim (theme asset, no CDN dependency).
 *
 * The oem-search-prototype.html mockup loads lucide from the unpkg CDN; the
 * production storefront must not depend on a third-party CDN at runtime, so
 * this file extracts ONLY the icons the OEM search engine actually uses
 * (assets/oem-search-engine.js + templates/oem-search-prototype.html,
 * "data-lucide=" union, plus the FINISH_ICON map's dynamic values: sparkles,
 * droplet, candy, square) from the lucide UMD bundle (v1.24.0) and renders
 * their SVG children inline — no network request, no external script tag.
 *
 * Defines window.lucide ONLY if it doesn't already exist, so a real lucide
 * include elsewhere in the theme always wins over this shim.
 *
 * createIcons() mirrors the real lucide.createIcons() contract closely enough
 * for this engine's usage: it replaces every `i[data-lucide]` / `span[data-lucide]`
 * element with an inline <svg>, keeping the `data-lucide` attribute and
 * merging the element's existing class list onto the generated
 * "lucide lucide-{name}" class (same as upstream lucide).
 *
 * 18 icons: arrow-down-wide-narrow, arrow-right, calendar, car, car-front, clock, droplet, hash, palette, plus, refresh-cw, scan-line, search, search-x, sparkles, triangle-alert, candy, square
 */
(function () {
  'use strict';
  if (typeof window === 'undefined') return;
  if (window.lucide) return; // real lucide (or another shim) already present — don't shadow it

  var ICONS = {
    "arrow-down-wide-narrow": "<path d='m3 16 4 4 4-4'/><path d='M7 20V4'/><path d='M11 4h10'/><path d='M11 8h7'/><path d='M11 12h4'/>",
    "arrow-right": "<path d='M5 12h14'/><path d='m12 5 7 7-7 7'/>",
    "calendar": "<path d='M8 2v4'/><path d='M16 2v4'/><rect width='18' height='18' x='3' y='4' rx='2'/><path d='M3 10h18'/>",
    "car": "<path d='M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2'/><circle cx='7' cy='17' r='2'/><path d='M9 17h6'/><circle cx='17' cy='17' r='2'/>",
    "car-front": "<path d='m21 8-2 2-1.5-3.7A2 2 0 0 0 15.646 5H8.4a2 2 0 0 0-1.903 1.257L5 10 3 8'/><path d='M7 14h.01'/><path d='M17 14h.01'/><rect width='18' height='8' x='3' y='10' rx='2'/><path d='M5 18v2'/><path d='M19 18v2'/>",
    "clock": "<circle cx='12' cy='12' r='10'/><path d='M12 6v6l4 2'/>",
    "droplet": "<path d='M12 22a7 7 0 0 0 7-7c0-2-1-3.9-3-5.5s-3.5-4-4-6.5c-.5 2.5-2 4.9-4 6.5C6 11.1 5 13 5 15a7 7 0 0 0 7 7z'/>",
    "hash": "<line x1='4' x2='20' y1='9' y2='9'/><line x1='4' x2='20' y1='15' y2='15'/><line x1='10' x2='8' y1='3' y2='21'/><line x1='16' x2='14' y1='3' y2='21'/>",
    "palette": "<path d='M12 22a1 1 0 0 1 0-20 10 9 0 0 1 10 9 5 5 0 0 1-5 5h-2.25a1.75 1.75 0 0 0-1.4 2.8l.3.4a1.75 1.75 0 0 1-1.4 2.8z'/><circle cx='13.5' cy='6.5' r='.5' fill='currentColor'/><circle cx='17.5' cy='10.5' r='.5' fill='currentColor'/><circle cx='6.5' cy='12.5' r='.5' fill='currentColor'/><circle cx='8.5' cy='7.5' r='.5' fill='currentColor'/>",
    "plus": "<path d='M5 12h14'/><path d='M12 5v14'/>",
    "refresh-cw": "<path d='M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8'/><path d='M21 3v5h-5'/><path d='M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16'/><path d='M8 16H3v5'/>",
    "scan-line": "<path d='M3 7V5a2 2 0 0 1 2-2h2'/><path d='M17 3h2a2 2 0 0 1 2 2v2'/><path d='M21 17v2a2 2 0 0 1-2 2h-2'/><path d='M7 21H5a2 2 0 0 1-2-2v-2'/><path d='M7 12h10'/>",
    "search": "<path d='m21 21-4.34-4.34'/><circle cx='11' cy='11' r='8'/>",
    "search-x": "<path d='m13.5 8.5-5 5'/><path d='m8.5 8.5 5 5'/><circle cx='11' cy='11' r='8'/><path d='m21 21-4.3-4.3'/>",
    "sparkles": "<path d='M11.017 2.814a1 1 0 0 1 1.966 0l1.051 5.558a2 2 0 0 0 1.594 1.594l5.558 1.051a1 1 0 0 1 0 1.966l-5.558 1.051a2 2 0 0 0-1.594 1.594l-1.051 5.558a1 1 0 0 1-1.966 0l-1.051-5.558a2 2 0 0 0-1.594-1.594l-5.558-1.051a1 1 0 0 1 0-1.966l5.558-1.051a2 2 0 0 0 1.594-1.594z'/><path d='M20 2v4'/><path d='M22 4h-4'/><circle cx='4' cy='20' r='2'/>",
    "triangle-alert": "<path d='m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3'/><path d='M12 9v4'/><path d='M12 17h.01'/>",
    "candy": "<path d='M10 7v10.9'/><path d='M14 6.1V17'/><path d='M16 7V3a1 1 0 0 1 1.707-.707 2.5 2.5 0 0 0 2.152.717 1 1 0 0 1 1.131 1.131 2.5 2.5 0 0 0 .717 2.152A1 1 0 0 1 21 8h-4'/><path d='M16.536 7.465a5 5 0 0 0-7.072 0l-2 2a5 5 0 0 0 0 7.07 5 5 0 0 0 7.072 0l2-2a5 5 0 0 0 0-7.07'/><path d='M8 17v4a1 1 0 0 1-1.707.707 2.5 2.5 0 0 0-2.152-.717 1 1 0 0 1-1.131-1.131 2.5 2.5 0 0 0-.717-2.152A1 1 0 0 1 3 16h4'/>",
    "square": "<rect width='18' height='18' x='3' y='3' rx='2'/>"
  };

  function mergedClass(name, existing) {
    var base = 'lucide lucide-' + name;
    if (!existing) return base;
    return (base + ' ' + existing).replace(/\s+/g, ' ').replace(/^\s+|\s+$/g, '');
  }

  function createIcons() {
    if (typeof document === 'undefined') return;
    var els = document.querySelectorAll('i[data-lucide], span[data-lucide]');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      var name = el.getAttribute('data-lucide');
      var inner = ICONS[name];
      if (inner == null) continue; // icon not in this shim's extracted set
      var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      svg.setAttribute('width', '24');
      svg.setAttribute('height', '24');
      svg.setAttribute('viewBox', '0 0 24 24');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('stroke', 'currentColor');
      svg.setAttribute('stroke-width', '2');
      svg.setAttribute('stroke-linecap', 'round');
      svg.setAttribute('stroke-linejoin', 'round');
      svg.setAttribute('data-lucide', name);
      svg.setAttribute('class', mergedClass(name, el.getAttribute('class')));
      var style = el.getAttribute('style');
      if (style) svg.setAttribute('style', style);
      svg.innerHTML = inner;
      if (el.parentNode) el.parentNode.replaceChild(svg, el);
    }
  }

  window.lucide = { createIcons: createIcons };
})();
